import cx from 'classnames';
import Link from 'next/link';
import Cookies from 'js-cookie';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';

import FeaturedLink from '../../../../../ui/links/featured';
import useModelVersionLink from '../../../../../ui/v2/vehicle-list/use-model-version-link';
import Search from '../../../../vehicle/search';
import FilteredList from '../../filtered-list';
import SearchInput from '../../search-input';
import saveUserCars from '../../../../../../services/user/save-cars';
import { useRouter } from 'next/router';
import useSelectVehicle from '../../../../../modules/vehicle-picker/use-selected-vehicle';

type VersionListProps = {
  vehicle: TCarInfo;
  versions: TResultAPICarsBySlug;
  stock: boolean;
  displayList?: boolean;
  displayInput?: boolean;
  numberOfColumns?: number;
  searchInput?: any;
  customClickFunction?: boolean;
  noResultsLink?: boolean;
};

const VersionList = ({
  versions,
  vehicle,
  stock,
  displayList = false,
  displayInput = true,
  numberOfColumns = 1,
  searchInput,
  customClickFunction = false,
  noResultsLink = true,
}: VersionListProps) => {
  const { t } = useTranslation();
  const { locale = 'en' } = useRouter();
  const [search, setSearch] = useState('');
  const setUserVehicle = useSelectVehicle();

  const linkProps = useModelVersionLink({
    isPicker: false,
    have_versions: false,
    slug: vehicle?.model?.slug as string,
  });

  const filteredVersions = useMemo(
    () => Search(search, versions.children),
    [search, versions]
  );

  useEffect(() => setSearch(searchInput), [searchInput]);

  return (
    <div
      className={cx(
        { 'py-[32px] lg:py-12 lg:pb-16': !displayList },
        'container mx-auto px-4'
      )}
      id="scroll_to_search_versions"
    >
      {displayInput && (
        <div
          className={cx(
            'w-full mx-auto max-lg:py-0 lg:p-0 sticky top-0 bg-white z-10'
          )}
        >
          <SearchInput
            search={search}
            placeholder={t('vehicle:versions.searchByVersionOrYear')}
            setSearch={setSearch}
            isFullWith={true}
            targetId='scroll_to_search_versions'
          />
        </div>
      )}
      {stock && (
        <div
          key="search-without-version"
          className="text-center lg:mt-0 pt-12 mx-auto container w-full flex justify-center gap-1"
        >
          <span className="lg:text-xl">{t(`vehicle:if-prefer`)}</span>
          <Link
            key="ignore-version"
            {...linkProps}
            passHref
            shallow
            scroll
            prefetch={false}
            legacyBehavior
            className="w-full"
          >
            <FeaturedLink
              variant="blackBold"
              className="max-lg:text-base text-center lg:!text-xl"
              onClick={() => {
                setUserVehicle(vehicle.model?.slug as string)
              }}
            >
              {t('vehicle:versions.continueWithoutVersion')}
            </FeaturedLink>
          </Link>
        </div>
      )}
      <div className={cx({ 'pt-[32px] lg:pt-12': !displayList })}>
        <FilteredList
          values={filteredVersions || []}
          isAllDisabled={!stock && !displayList}
          headerTag="h3"
          itemTag="h4"
          displayList={displayList}
          numberOfColumns={numberOfColumns}
          customClickFunction={customClickFunction}
          listType={'VERSIONS'}
          noResultsLink={noResultsLink}
        />
      </div>
    </div>
  );
};

export default VersionList;
